/**
 * The travel / insurance split every finance and report surface makes.
 *
 * A Faye premium rides inside a booking's `total_amount`, `grand_total` and
 * `grand_total_with_hold` (migration 156), so a plan total always carries it,
 * and every number that measures Defera's own business has to take it back
 * out. The premium is collected for Faye: it is not revenue, not a fee, not
 * base fare and not profit. Defera's only insurance income is Faye's
 * commission, which nothing records yet.
 *
 * The house rules, applied the same way on every surface:
 *
 *   1. Revenue, GMV / booked value, take rate, margin, profit, base fare,
 *      ticket cost, miles, backer and investor fees, rankings and "total
 *      spent" run on the TRAVEL figures (`withoutInsurancePremium`).
 *   2. Metrics built on cash collected (collected %, fees struck on cash,
 *      recovery ratios, cash-kept profit) strip the premium's share of that
 *      cash (`premiumShareOfCashCents`).
 *   3. What is owed or still to collect keeps the premium: the customer owes
 *      it. Money going OUT at ticketing adds the premium for a policy Defera
 *      has not bought yet (`premiumDueAtTicketingCents`).
 *   4. Processing is charged on every dollar collected, premium included.
 *   5. Collections may pursue the premium only once the policy was issued
 *      (`collectibleBalance`).
 *   6. Airline cost / actual cost never includes the premium.
 *
 * Pure apart from the two small loaders at the bottom, which take the
 * Supabase client as an argument. Safe to import from the Vite client: the
 * only import is the shared rules module the client already bundles.
 *
 * Every function returns exactly what the caller had before for a booking
 * without a premium, which today is every booking.
 */
import {
  INSURANCE_STATUS,
  insuranceAddedAfterBooking,
  insurancePremiumCents,
  mayHaveTripInsurance,
  premiumCollectedCents,
  premiumShareAfterAddCents,
  upfrontAddPremiumCents,
  withoutInsurancePremium,
} from '../supabase/functions/_shared/trip-insurance.js';

export { INSURANCE_STATUS, insurancePremiumCents, premiumCollectedCents, withoutInsurancePremium };

const centsOf = (dollars) => {
  const n = Number(dollars);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/** The row's plan total in cents, premium included: what the customer owes in all. */
export function planTotalCents(row) {
  const r = row || {};
  return centsOf(
    r.grand_total_with_hold ?? r.grandTotalWithHold ?? r.grand_total ?? r.grandTotal ?? r.total_amount ?? r.totalAmount,
  );
}

/**
 * The deposit the plan was built on, in cents, or null when the row does not
 * say. The deposit never carries premium (the premium rides on the payments
 * after it), which is what makes the precise premium split possible.
 */
export function planDepositCents(row) {
  const fd = row?.flight_data || row?.flightData || {};
  const raw = fd?.payment_schedule?.deposit_amount_cents ?? fd?.payment_schedule?.depositAmountCents;
  if (raw == null || raw === '') return null;
  const n = Math.round(Number(raw));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * The premium's share of `paidCents` collected on a booking, in cents.
 *
 * With the deposit known it is exact (`premiumCollectedCents`: the premium is
 * spread evenly over the payments after the deposit). Without it, the cash is
 * split pro rata by premium / plan total. 0 for an uninsured booking, and
 * never more than the premium or the cash.
 *
 * @param {object} p
 * @param {object} p.row             the booking row (premium and deposit are read off it)
 * @param {number} p.paidCents       cash collected toward the plan, deposit included
 * @param {number} [p.premiumCents]  override the row's premium
 * @param {number} [p.planTotalCents]  override the row's plan total (premium included)
 * @param {number|null} [p.depositCents]  override the row's deposit; null = unknown
 */
export function premiumShareOfCashCents({ row, paidCents, premiumCents, planTotalCents: total, depositCents } = {}) {
  const premium = premiumCents != null ? Math.max(0, Math.round(Number(premiumCents) || 0)) : insurancePremiumCents(row);
  const paid = Math.max(0, Math.round(Number(paidCents) || 0));
  if (!premium || !paid) return 0;
  const plan = total != null ? Math.round(Number(total) || 0) : planTotalCents(row);
  if (!(plan > 0)) return 0;
  // Added after booking and spread over the payments left: only payments
  // after the add carry it. Spreading it from the deposit, as at checkout,
  // put premium in cash paid before the add, and collections pursued a
  // defaulted trip for more travel balance than it owed (review of #109).
  const added = insuranceAddedAfterBooking(row);
  if (added?.mode === 'spread' && added.plan_paid_before_cents != null) {
    return Math.min(paid, premiumShareAfterAddCents({ premiumCents: premium, planTotalCents: plan, planPaidBeforeCents: added.plan_paid_before_cents, paidCents: paid }));
  }
  const deposit = depositCents !== undefined ? depositCents : planDepositCents(row);
  if (deposit != null && plan - deposit > 0) {
    return Math.min(paid, premiumCollectedCents({
      premiumCents: premium, planTotalCents: plan, depositCents: deposit, paidCents: paid,
    }));
  }
  return Math.min(premium, paid, Math.round((premium * Math.min(paid, plan)) / plan));
}

/**
 * The share of each payment AFTER the deposit that is premium (0..1). Future
 * installments are all post-deposit, so this is what splits scheduled inflow
 * into travel and premium. Pro rata on the plan total when the deposit is
 * unknown.
 */
export function premiumShareOfInstallment(row, { planTotalCents: total, depositCents } = {}) {
  const premium = insurancePremiumCents(row);
  if (!premium) return 0;
  const plan = total != null ? Math.round(Number(total) || 0) : planTotalCents(row);
  if (!(plan > 0)) return 0;
  const deposit = depositCents !== undefined ? depositCents : planDepositCents(row);
  const financed = deposit != null ? plan - deposit : plan;
  if (!(financed > 0)) return 0;
  return Math.min(1, premium / financed);
}

// ─── The policy ─────────────────────────────────────────────────────────────

/** Statuses at which Defera has bought the policy from Faye. */
export const ISSUED_POLICY_STATUSES = Object.freeze([INSURANCE_STATUS.ISSUED, INSURANCE_STATUS.NEEDS_CANCELLATION]);

/** Statuses at which the policy will never be bought. */
const CLOSED_UNBOUGHT_STATUSES = new Set([
  INSURANCE_STATUS.REFUND_DUE, INSURANCE_STATUS.REFUNDED, INSURANCE_STATUS.CANCELLED,
]);

export const isPolicyIssued = (status) => ISSUED_POLICY_STATUSES.includes(status);

/**
 * The policy status a loader or a booking endpoint attached to the row, or
 * null when nothing did (callers then treat an insured booking as pending,
 * the status every policy starts in).
 */
export function policyStatusOf(row) {
  if (!row || typeof row !== 'object') return null;
  if (typeof row.insurance_policy_status === 'string') return row.insurance_policy_status;
  const ti = row.trip_insurance ?? row.tripInsurance;
  const policy = Array.isArray(ti) ? ti.find((p) => p && typeof p === 'object') : ti;
  return typeof policy?.status === 'string' ? policy.status : null;
}

/**
 * The premium Defera still has to pay Faye when it buys the policy, in cents:
 * the premium while the policy is pending (or its status is unknown), 0 once
 * it is bought or will never be.
 */
export function premiumDueAtTicketingCents(row, status = policyStatusOf(row)) {
  // A premium charged upfront (insurance added to a paid-off trip) is owed to
  // Faye too, though it never joined the plan's totals.
  const premium = insurancePremiumCents(row) || upfrontAddPremiumCents(row);
  if (!premium) return 0;
  if (isPolicyIssued(status) || CLOSED_UNBOUGHT_STATUSES.has(status)) return 0;
  return premium;
}

/**
 * What a collections agency may pursue on a defaulted plan.
 *
 * Contracted and paid both come in plan terms (premium inside). When the
 * policy was issued Defera paid Faye, so the premium is part of the debt and
 * nothing changes. Otherwise the premium was never bought: it comes out of
 * the contracted total, and the premium's share of what was paid comes out
 * of the payments (that cash goes back to the customer), so the principal is
 * the travel balance alone.
 *
 * @returns {{ contractedCents: number|null, paidCents: number, principalCents: number|null,
 *             excludedPremiumCents: number }}
 */
export function collectibleBalance({ row, contractedCents, paidCents, policyStatus = policyStatusOf(row), depositCents } = {}) {
  const contracted = contractedCents == null ? null : Math.round(Number(contractedCents) || 0);
  const paid = Math.max(0, Math.round(Number(paidCents) || 0));
  const premium = insurancePremiumCents(row);
  const plain = {
    contractedCents: contracted,
    paidCents: paid,
    principalCents: contracted == null ? null : Math.max(0, contracted - paid),
    excludedPremiumCents: 0,
  };
  if (!premium || contracted == null || isPolicyIssued(policyStatus)) return plain;
  const premiumPaid = premiumShareOfCashCents({
    row, paidCents: paid, premiumCents: premium, planTotalCents: contracted, depositCents,
  });
  const travelContracted = Math.max(0, contracted - premium);
  const travelPaid = Math.max(0, paid - premiumPaid);
  return {
    contractedCents: travelContracted,
    paidCents: travelPaid,
    principalCents: Math.max(0, travelContracted - travelPaid),
    excludedPremiumCents: premium,
  };
}

// ─── Risk ───────────────────────────────────────────────────────────────────

/**
 * Insurance fields for a risk assessment of `row`, given the plan total and
 * the cash paid toward it (both premium included, in cents). Null without a
 * premium, so an uninsured trip's assessment carries no new keys.
 *
 * `travel_paid_percentage` is the paid share measured on travel (premium out
 * of both sides), the basis every "how much of the trip is paid" figure uses.
 */
export function insuranceRiskFields(row, { planTotalCents: total, paidCents } = {}) {
  const premium = insurancePremiumCents(row);
  if (!premium) return null;
  const plan = total != null ? Math.round(Number(total) || 0) : planTotalCents(row);
  const paid = Math.max(0, Math.round(Number(paidCents) || 0));
  const premiumPaid = premiumShareOfCashCents({ row, paidCents: paid, premiumCents: premium, planTotalCents: plan });
  const travelTotal = plan - premium;
  const travelPaid = paid - premiumPaid;
  return {
    insurance_premium: premium / 100,
    insurance_premium_paid: premiumPaid / 100,
    insurance_premium_outstanding: (premium - premiumPaid) / 100,
    insurance_policy_status: policyStatusOf(row) || INSURANCE_STATUS.PENDING,
    travel_paid_percentage: travelTotal > 0 ? Math.round((travelPaid / travelTotal) * 100) : 0,
  };
}

/**
 * Dollars of a trip's remaining balance that are premium for a policy Defera
 * has not bought. Still owed, but never a loss if the customer stops paying:
 * the policy is simply not bought. 0 once it is (Defera has paid Faye) or
 * without insurance. Reads the `insurance_*` fields of insuranceRiskFields.
 */
export function unboughtPremiumOutstandingDollars(features) {
  const outstanding = Number(features?.insurance_premium_outstanding) || 0;
  if (!(outstanding > 0) || isPolicyIssued(features?.insurance_policy_status)) return 0;
  return outstanding;
}

// ─── Loading ────────────────────────────────────────────────────────────────

export const PREMIUM_COLUMN = 'insurance_premium_cents';

/** PostgREST's answer to a select naming a column the table doesn't have. */
export function isMissingPremiumColumn(error) {
  if (!error) return false;
  const text = `${error.message || ''} ${error.details || ''} ${error.hint || ''}`;
  return text.includes(PREMIUM_COLUMN) && (error.code === '42703' || error.code === 'PGRST204' || /does not exist|could not find/i.test(text));
}

/**
 * Run a bookings select with `insurance_premium_cents` added to an explicit
 * column list. Before migration 156 the column doesn't exist and a select
 * naming it fails outright (the whole query, every row), so this retries once
 * without it rather than taking a report down.
 *
 * @param {string} columns       the caller's column list
 * @param {(cols: string) => PromiseLike<{data:any,error:any}>} run  builds and awaits the query
 */
export async function selectWithPremiumColumn(columns, run) {
  const first = await run(`${columns}, ${PREMIUM_COLUMN}`);
  if (first?.error && isMissingPremiumColumn(first.error)) return run(columns);
  return first;
}

/** Before migration 156 the policy table doesn't exist: quiet, not a failure. */
const MISSING_TABLE = /does not exist|could not find the table/i;

/**
 * Policy status, premium and Faye's price for each insured booking id.
 * Only the ids passed are looked up, in chunks; bookings without a premium
 * should never be passed (they have no policy), so an uninsured book costs no
 * query at all. Never throws: a missing table or a failed read returns what
 * it has, and callers then treat an insured booking as pending.
 *
 * @returns {Promise<Map<string, { status: string, premium_cents: number, faye_premium_cents: number|null }>>}
 */
export async function loadPolicyStatuses(supabase, bookingIds, { logger = null } = {}) {
  const ids = [...new Set((bookingIds || []).filter(Boolean))];
  const out = new Map();
  if (!ids.length || !supabase) return out;
  for (let i = 0; i < ids.length; i += 100) {
    try {
      const { data, error } = await supabase
        .from('trip_insurance_policies')
        .select('booking_id, status, premium_cents, faye_premium_cents')
        .in('booking_id', ids.slice(i, i + 100));
      if (error) throw error;
      for (const p of data || []) {
        if (p?.booking_id) out.set(p.booking_id, p);
      }
    } catch (err) {
      if (!MISSING_TABLE.test(String(err?.message))) {
        logger?.warn?.('InsuranceFinance', 'policy status lookup failed; insured bookings read as pending', {
          error: err?.message, bookings: ids.length,
        });
      }
      break;
    }
  }
  return out;
}

/**
 * Stamp `insurance_policy_status` on every insured row (pending when no policy
 * row was found). Returns the same rows. Uninsured rows are untouched.
 */
export async function attachPolicyStatuses(supabase, rows, { logger = null } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  // Rows with a premium, or a policy added after booking (an upfront add has
  // no premium column: its marker is the sign).
  const insured = list.filter((r) => r?.id && mayHaveTripInsurance(r));
  if (!insured.length) return list;
  const byBooking = await loadPolicyStatuses(supabase, insured.map((r) => r.id), { logger });
  for (const r of insured) {
    r.insurance_policy_status = byBooking.get(r.id)?.status || INSURANCE_STATUS.PENDING;
  }
  return list;
}
